import { z } from 'zod';

/**
 * Identifier value objects.
 *
 * The legacy `@id` grammar (`stage:S2`, `act:S2.1`, `gate:G2`, `tmpl:HLD`,
 * `lens:APP`, `role:pm`, `agent:arch`, `dpem:DPEM-Agentic`) is canonical and
 * immutable (00-ARCHITECTURE decision 8), so it is validated rather than
 * replaced. Each entity kind declares the prefixes it accepts; the shape is
 * `prefix:local` where local starts alphanumeric and may contain `. - _`.
 *
 * Every id is also branded `ElementId`, which makes a concrete id assignable
 * wherever a polymorphic reference target is expected (a gate's `loopsTo` may
 * point at a stage or an activity) without erasing the specific brand.
 */
const SEGMENT = String.raw`[A-Za-z0-9][A-Za-z0-9._-]*`;

/**
 * `local`, or `namespace/local` for an entry a client brought into one adoption:
 * `tool:vrg/backstage-idp`.
 *
 * The namespace is what keeps a session-scoped catalogue entry recognisable in a
 * raw JSON-LD document with no lookup, and it is why the widening is here rather
 * than in the catalogue's own id types: a client tool is referenced from
 * `usesTool`, so it has to satisfy `ElementIdSchema` — the grammar every
 * reference array is validated against — or the published document fails
 * MTH-001 on a node that is in fact present.
 *
 * Exactly one optional segment, not arbitrary nesting. `tool:vrg/ghes` is a
 * client's instance of a platform tool; `tool:vrg/emea/ghes` would be a
 * hierarchy this product does not have, and accepting it now would make it
 * something a later tenant model has to keep accepting.
 */
const LOCAL_PART = String.raw`(?:${SEGMENT}/)?${SEGMENT}`;

/** Prefixes accepted per entity kind. Order is significant only in messages. */
export const ID_PREFIXES = {
  /**
   * `dpse` first and `dpem` still accepted. The product was renamed from DPEM to DPSE
   * (Digital Product and Software Engineering), so a method authored from now on should
   * be minted under the current brand — but a canonical id is immutable once published
   * (00-ARCHITECTURE decision 8) and the seeded methods, every published snapshot's
   * checksum and every workshop and plan that cites one are keyed on `dpem:`. Accepting
   * both is the only version of this rename that does not rewrite history.
   */
  method: ['dpse', 'dpem', 'method'],
  phase: ['phase'],
  stage: ['stage'],
  workPractice: ['wp', 'practice'],
  activity: ['act', 'activity'],
  role: ['role', 'agent'],
  gate: ['gate'],
  deliverable: ['tmpl', 'deliv'],
  templateVersion: ['tv'],
  lens: ['lens'],
  ceremony: ['comm', 'ceremony'],
  /** `dpem` stays first here: these ids are seeded vocabulary, never minted at runtime. */
  communicationArtifact: ['dpem', 'artifact', 'dpse'],
  tool: ['tool'],
  asset: ['asset'],
  /**
   * `agt:` and not `agent:` on purpose: `agent:arch` is already taken by the
   * agentic *roles* in the seed graph. A catalogued Agent is a different entity
   * from the role an agent plays, and two entities may not share an `@id`.
   */
  agent: ['agt'],
  agentSkill: ['skill'],
  mcpServer: ['mcp'],
  tailoringDecision: ['tdec'],
} as const satisfies Record<string, readonly string[]>;

export type IdKind = keyof typeof ID_PREFIXES;

const ALL_PREFIXES: readonly string[] = [
  ...new Set(Object.values(ID_PREFIXES).flat()),
].sort();

function patternFor(prefixes: readonly string[]): RegExp {
  return new RegExp(`^(?:${prefixes.join('|')}):${LOCAL_PART}$`);
}

function messageFor(kind: string, prefixes: readonly string[]): string {
  return `${kind} @id must look like ${prefixes.map((p) => `${p}:<local>`).join(' or ')}`;
}

function brandedId<Brand extends string>(kind: IdKind, brand: Brand) {
  const prefixes = ID_PREFIXES[kind];
  return z
    .string()
    .regex(patternFor(prefixes), messageFor(brand, prefixes))
    .brand<'ElementId'>()
    .brand<Brand>();
}

/** Any well-formed DPSE identifier, whatever it points at. */
export const ElementIdSchema = z
  .string()
  .regex(patternFor(ALL_PREFIXES), messageFor('ElementId', ALL_PREFIXES))
  .brand<'ElementId'>();

export const MethodIdSchema = brandedId('method', 'MethodId');
export const PhaseIdSchema = brandedId('phase', 'PhaseId');
export const StageIdSchema = brandedId('stage', 'StageId');
export const WorkPracticeIdSchema = brandedId('workPractice', 'WorkPracticeId');
export const ActivityIdSchema = brandedId('activity', 'ActivityId');
export const RoleIdSchema = brandedId('role', 'RoleId');
export const GateIdSchema = brandedId('gate', 'GateId');
export const DeliverableIdSchema = brandedId('deliverable', 'DeliverableId');
export const TemplateVersionIdSchema = brandedId('templateVersion', 'TemplateVersionId');
export const LensIdSchema = brandedId('lens', 'LensId');
export const CeremonyIdSchema = brandedId('ceremony', 'CeremonyId');
export const CommunicationArtifactIdSchema = brandedId(
  'communicationArtifact',
  'CommunicationArtifactId',
);
export const ToolIdSchema = brandedId('tool', 'ToolId');
export const AssetIdSchema = brandedId('asset', 'AssetId');
export const AgentIdSchema = brandedId('agent', 'AgentId');
export const AgentSkillIdSchema = brandedId('agentSkill', 'AgentSkillId');
export const McpServerIdSchema = brandedId('mcpServer', 'McpServerId');
export const TailoringDecisionIdSchema = brandedId('tailoringDecision', 'TailoringDecisionId');

export type ElementId = z.infer<typeof ElementIdSchema>;
export type MethodId = z.infer<typeof MethodIdSchema>;
export type PhaseId = z.infer<typeof PhaseIdSchema>;
export type StageId = z.infer<typeof StageIdSchema>;
export type WorkPracticeId = z.infer<typeof WorkPracticeIdSchema>;
export type ActivityId = z.infer<typeof ActivityIdSchema>;
export type RoleId = z.infer<typeof RoleIdSchema>;
export type GateId = z.infer<typeof GateIdSchema>;
export type DeliverableId = z.infer<typeof DeliverableIdSchema>;
export type TemplateVersionId = z.infer<typeof TemplateVersionIdSchema>;
export type LensId = z.infer<typeof LensIdSchema>;
export type CeremonyId = z.infer<typeof CeremonyIdSchema>;
export type CommunicationArtifactId = z.infer<typeof CommunicationArtifactIdSchema>;
export type ToolId = z.infer<typeof ToolIdSchema>;
export type AssetId = z.infer<typeof AssetIdSchema>;
export type AgentId = z.infer<typeof AgentIdSchema>;
export type AgentSkillId = z.infer<typeof AgentSkillIdSchema>;
export type McpServerId = z.infer<typeof McpServerIdSchema>;
export type TailoringDecisionId = z.infer<typeof TailoringDecisionIdSchema>;

export const elementId = (value: string): ElementId => ElementIdSchema.parse(value);
export const methodId = (value: string): MethodId => MethodIdSchema.parse(value);
export const phaseId = (value: string): PhaseId => PhaseIdSchema.parse(value);
export const stageId = (value: string): StageId => StageIdSchema.parse(value);
export const workPracticeId = (value: string): WorkPracticeId => WorkPracticeIdSchema.parse(value);
export const activityId = (value: string): ActivityId => ActivityIdSchema.parse(value);
export const roleId = (value: string): RoleId => RoleIdSchema.parse(value);
export const gateId = (value: string): GateId => GateIdSchema.parse(value);
export const deliverableId = (value: string): DeliverableId => DeliverableIdSchema.parse(value);
export const templateVersionId = (value: string): TemplateVersionId =>
  TemplateVersionIdSchema.parse(value);
export const lensId = (value: string): LensId => LensIdSchema.parse(value);
export const ceremonyId = (value: string): CeremonyId => CeremonyIdSchema.parse(value);
export const communicationArtifactId = (value: string): CommunicationArtifactId =>
  CommunicationArtifactIdSchema.parse(value);
export const toolId = (value: string): ToolId => ToolIdSchema.parse(value);
export const assetId = (value: string): AssetId => AssetIdSchema.parse(value);
export const agentId = (value: string): AgentId => AgentIdSchema.parse(value);
export const agentSkillId = (value: string): AgentSkillId => AgentSkillIdSchema.parse(value);
export const mcpServerId = (value: string): McpServerId => McpServerIdSchema.parse(value);
export const tailoringDecisionId = (value: string): TailoringDecisionId =>
  TailoringDecisionIdSchema.parse(value);

/**
 * The same grammar, asked rather than enforced.
 *
 * A form has to know whether what an author typed is a well-formed id *before* it
 * builds a command, because the alternative is what shipped: the client's own
 * `checked()` threw a raw zod issue array and the surface showed it verbatim, so an
 * author who typed a role's *name* into a role-id field read
 * `{"origin":"string","code":"invalid_format", …}` instead of "use role:<local>".
 *
 * These three exist so the answer comes from this file and not from a regex copied
 * into a form module, where it would drift the first time a prefix is added.
 */
const PATTERNS: Readonly<Record<IdKind, RegExp>> = Object.freeze(
  Object.fromEntries(
    (Object.keys(ID_PREFIXES) as IdKind[]).map((kind) => [kind, patternFor(ID_PREFIXES[kind])]),
  ) as Record<IdKind, RegExp>,
);

/** Whether `value` is a well-formed id for `kind`. Never throws. */
export function isWellFormedId(kind: IdKind, value: string): boolean {
  return PATTERNS[kind].test(value);
}

/** The shape, for a hint or an error: `role:<local> or agent:<local>`. */
export function idShapeHint(kind: IdKind): string {
  return ID_PREFIXES[kind].map((prefix) => `${prefix}:<local>`).join(' or ');
}

/**
 * The prefix a *new* id of this kind should be minted with — the first one declared,
 * which is why `ID_PREFIXES` says order matters. The others stay accepted on read: a
 * seed graph full of `agent:arch` roles must not become invalid because `role:` reads
 * better in a picker.
 */
export function canonicalPrefix(kind: IdKind): string {
  return ID_PREFIXES[kind][0] as string;
}

/** The prefix of an identifier, or `undefined` if it is not prefixed. */
export function idPrefix(value: string): string | undefined {
  const separator = value.indexOf(':');
  return separator > 0 ? value.slice(0, separator) : undefined;
}
